import {
  Component,
  Element,
  Event,
  EventEmitter,
  Host,
  h,
} from '@stencil/core';
import { HTMLStencilElement, Prop, State, Watch } from '@stencil/core/internal';
import { Language } from '../../enums/const-enums';
import { logd } from '../../utils/utils';
import {
  RassPasswordTranslations,
  rassPasswordTranslations,
} from './i18n/rass-password.i18n';

let hasInitialized = false;

@Component({
  tag: 'rass-password',
  styleUrl: 'rass-password.scss',
  shadow: false,
})
export class RassPasswordComponent {
  @Element() element!: HTMLStencilElement;
  @Prop() language: Language;
  @Prop() isOneTimePassword = false;
  @Prop() isBlockingUserInput: boolean;
  @Prop() hasServerError: boolean;
  @Prop() activeAccount: string;
  @State() password: string;
  @State() isPasswordFormatValid: boolean;
  @Event({ eventName: 'rass:password:submit' })
  submitEvent: EventEmitter<string>;

  componentDidRender(): void {
    if (!this.isBlockingUserInput) {
      setTimeout(() => {
        this.rdsInputElem
          .setFocus()
          .catch((err: Error) => logd(() => console.error(err)));
      }, 25);
    }
    if (!hasInitialized) {
      hasInitialized = true;
    }
  }

  @Watch('hasServerError')
  statusTextChanged(isNewValue: boolean): void {
    if (isNewValue) {
      this.rdsInputElem
        .validate()
        .catch((err: Error) => logd(() => console.error(err)));
    }
  }

  handleInput(): void {
    this.password = this.rdsInputElem.value;
    this.isPasswordFormatValid = false;

    if (this.password.length > 7) {
      this.isPasswordFormatValid = true;
      this.rdsInputElem
        .validate()
        .catch((err: Error) => logd(() => console.error(err)));
    }
  }

  handleEnterKey(event: CustomEvent<KeyboardEvent>): void {
    if (event.detail.key === 'Enter') {
      this.rdsInputElem
        .validate()
        .then((isValid) => {
          if (isValid) {
            this.handleSubmit();
          }
        })
        .catch((err: Error) => logd(() => console.error(err)));
    }
  }

  handleSubmit(): void {
    this.rdsInputElem
      .validate()
      .then((isValid: boolean) => {
        if (isValid) {
          this.submitEvent.emit(this.password);
        }
      })
      .catch((err: Error) => logd(() => console.error(err)));
  }

  get rdsInputElem(): HTMLRdsInputElement {
    return this.element.querySelector<HTMLRdsInputElement>('rds-input');
  }

  get translations(): RassPasswordTranslations {
    return (
      rassPasswordTranslations[this.language] ||
      rassPasswordTranslations[Language.EN]
    );
  }

  @Event({
    eventName: 'rass:password:init',
  })
  passwordInit: EventEmitter;

  componentWillRender(): void {
    if (!hasInitialized) {
      this.passwordInit.emit();
    }
  }

  render(): void {
    return (
      <Host>
        <div class={{ 'rds-display-none': !hasInitialized }}>
          <rds-input-wrapper>
            <rds-input
              id="pwd-input"
              label={
                this.isOneTimePassword
                  ? this.translations.inputLabel.otp
                  : this.translations.inputLabel.default
              }
              name="rass-password-input"
              slot="input"
              type="password"
              disabled={this.isBlockingUserInput}
              onRdsInput={(): void => this.handleInput()}
              onRdsKeyDown={(event): void => this.handleEnterKey(event)}
              min-length="8"
              max-length="64"
            ></rds-input>
            <custom-error
              slot="error"
              for="rass-password-input"
              when="valueMissing"
            >
              {this.translations.valueMissing}
            </custom-error>
            <custom-error
              slot="error"
              for="rass-password-input"
              when="tooShort"
            >
              {this.translations.tooShort}
            </custom-error>
            <custom-error slot="error" for="rass-password-input" when="tooLong">
              {this.translations.tooLong}
            </custom-error>
          </rds-input-wrapper>
          <rds-button
            id="password-submit"
            disabled={!this.isPasswordFormatValid || this.isBlockingUserInput}
            onRdsClick={(): void => this.handleSubmit()}
          >
            {this.translations.buttonLabel}
          </rds-button>
        </div>
        <rds-spinner
          class={{
            'rds-display-none': hasInitialized,
            'rds-display-flex': !hasInitialized,
            'rds-justify-content-center': true,
          }}
        ></rds-spinner>
      </Host>
    ) as void;
  }
}
